(function (window, document) {
  "use strict";

  const FORM_SELECTOR = "form[data-search-form], form.search";
  const RESULT_WRAPPER_SELECTOR =
    "[data-predictive-search-results], #predictive-search";
  const RESULT_CONTAINER_SELECTOR = "[data-predictive-search]";
  const SELECTABLE_ITEM_SELECTOR =
    'li, [role="option"], .bls__product-item';
  const CACHE_LIMIT = 40;
  const DEBOUNCE_DELAY = 300;
  const formStateByForm = new WeakMap();
  const mountedFormStates = new Set();
  const scopeBindingByScope = new WeakMap();
  const mountedScopeBindings = new Set();
  const responseCache = new Map();
  let formSequence = 0;
  let searchOwnsOverlay = false;

  function elementsWithin(scope, selector) {
    if (!scope) return [];
    const elements = [];
    if (scope.matches && scope.matches(selector)) elements.push(scope);
    if (scope.querySelectorAll) {
      scope.querySelectorAll(selector).forEach((element) => {
        if (!elements.includes(element)) elements.push(element);
      });
    }
    return elements;
  }

  function scopeContains(scope, element) {
    return (
      scope === element ||
      scope === document ||
      Boolean(scope && scope.contains && scope.contains(element))
    );
  }

  function resolveRoot(form) {
    return (
      form.closest(".search__type-popup") ||
      form.closest(".bls__canvas") ||
      form.closest(".block-search-full") ||
      form.closest(".header_search") ||
      form.parentElement
    );
  }

  function resolveSectionId(form, root) {
    const popupRoot =
      form.closest(".search_type_popup") ||
      (root.matches && root.matches(".search__type-popup")) ||
      (root.querySelector && root.querySelector(".search__type-popup"));
    return popupRoot ? "search-predictive-grid" : "search-predictive-list";
  }

  function isSuggestSearch(form) {
    return Boolean(form.closest(".predictive_search_suggest"));
  }

  function normalizeQuery(query) {
    return query.trim().replace(/\s+/g, " ").toLowerCase();
  }

  function requestQuery(state, query) {
    const category = state.form.querySelector('[name="category"]');
    const categoryValue = category && category.value ? category.value.trim() : "";
    return categoryValue
      ? `product_type:${categoryValue} AND ${query}`
      : query;
  }

  function cacheKey(state, query) {
    return `${state.sectionId}|${state.suggest ? "suggest" : "search"}|${normalizeQuery(
      requestQuery(state, query)
    )}`;
  }

  function readCache(key) {
    if (!responseCache.has(key)) return null;
    const markup = responseCache.get(key);
    responseCache.delete(key);
    responseCache.set(key, markup);
    return markup;
  }

  function writeCache(key, markup) {
    if (responseCache.has(key)) responseCache.delete(key);
    responseCache.set(key, markup);
    if (responseCache.size > CACHE_LIMIT) {
      responseCache.delete(responseCache.keys().next().value);
    }
  }

  function listen(state, target, eventName, handler, options) {
    target.addEventListener(eventName, handler, options);
    state.listeners.push([target, eventName, handler, options]);
  }

  function setLoading(state, loading) {
    state.form.classList.toggle("loading", loading);
    state.resultWrapper.classList.toggle("loading", loading);
  }

  function resetSelection(state) {
    state.resultContainer
      .querySelectorAll('[aria-selected="true"]')
      .forEach((selected) => selected.setAttribute("aria-selected", "false"));
    state.input.setAttribute("aria-activedescendant", "");
  }

  function openResults(state) {
    state.input.setAttribute("aria-expanded", "true");
    state.isOpen = true;
  }

  function closeResults(state, clearResults) {
    if (clearResults) {
      state.resultWrapper.classList.remove("results");
      if (state.quickSearch) state.quickSearch.classList.remove("d-none");
      state.renderedQuery = "";
    }
    resetSelection(state);
    state.input.setAttribute("aria-expanded", "false");
    state.resultContainer.removeAttribute("style");
    state.isOpen = false;
  }

  function invalidateRequest(state) {
    state.generation += 1;
    if (state.timer) {
      window.clearTimeout(state.timer);
      state.timer = null;
    }
    if (state.controller) {
      state.controller.abort();
      state.controller = null;
    }
    return state.generation;
  }

  function clearQuery(state) {
    invalidateRequest(state);
    state.currentQuery = "";
    setLoading(state, false);
    closeResults(state, true);
  }

  function activateRenderedContent(state) {
    const swatches = window.BlsColorSwatchesShopify;
    if (swatches && typeof swatches.activateScope === "function") {
      swatches.activateScope(state.resultContainer);
    }
    const lazyImages = window.BlsLazyloadImg;
    if (lazyImages && typeof lazyImages.init === "function") {
      lazyImages.init();
    }
  }

  function renderResults(state, markup, query, generation) {
    if (!state.mounted || state.generation !== generation) return;
    state.resultContainer.innerHTML = markup;
    state.resultWrapper.classList.add("results");
    if (state.quickSearch) state.quickSearch.classList.add("d-none");
    state.renderedQuery = normalizeQuery(requestQuery(state, query));
    setLoading(state, false);
    openResults(state);
    activateRenderedContent(state);
  }

  function clearFailedResults(state, generation) {
    if (!state.mounted || state.generation !== generation) return;
    setLoading(state, false);
    closeResults(state, true);
  }

  function buildRequestUrl(state, query) {
    const routeTable = window.routes || {};
    const base = state.suggest
      ? routeTable.predictive_search_url || routeTable.search_url || "/search"
      : routeTable.search_url || "/search";
    return `${base}?q=${encodeURIComponent(
      requestQuery(state, query)
    )}&resources[type]=product,page,article,query,collection&section_id=${state.sectionId}`;
  }

  function requestResults(state, query, generation) {
    if (!state.mounted || state.generation !== generation) return;
    const key = cacheKey(state, query);
    const cachedMarkup = readCache(key);
    setLoading(state, true);
    if (cachedMarkup !== null) {
      renderResults(state, cachedMarkup, query, generation);
      return;
    }

    const controller = new window.AbortController();
    state.controller = controller;
    window
      .fetch(buildRequestUrl(state, query), { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Predictive search HTTP ${response.status}`);
        return response.text();
      })
      .then((text) => {
        if (!state.mounted || state.generation !== generation) return;
        const parsed = new window.DOMParser().parseFromString(text, "text/html");
        const section = parsed.querySelector(
          `#shopify-section-${state.sectionId}`
        );
        if (!section) {
          clearFailedResults(state, generation);
          return;
        }
        writeCache(key, section.innerHTML);
        renderResults(state, section.innerHTML, query, generation);
      })
      .catch(() => {
        clearFailedResults(state, generation);
      })
      .finally(() => {
        if (
          state.mounted &&
          state.generation === generation &&
          state.controller === controller
        ) {
          state.controller = null;
        }
      });
  }

  function scheduleQuery(state, immediate) {
    const query = state.input.value.trim();
    state.currentQuery = query;
    if (!query) {
      clearQuery(state);
      return;
    }

    const generation = invalidateRequest(state);
    state.currentQuery = query;
    if (immediate) {
      requestResults(state, query, generation);
      return;
    }
    state.timer = window.setTimeout(() => {
      state.timer = null;
      requestResults(state, query, generation);
    }, DEBOUNCE_DELAY);
  }

  function selectableItems(state) {
    return Array.from(
      state.resultContainer.querySelectorAll(SELECTABLE_ITEM_SELECTOR)
    ).filter(
      (item) =>
        !(item.matches("li") && item.closest(".bls__product-item")) &&
        (item.matches('a, button, [role="option"]') ||
          Boolean(item.querySelector("a, button")))
    );
  }

  function selectedAction(state) {
    const selected = state.resultContainer.querySelector(
      '[aria-selected="true"]'
    );
    if (!selected) return null;
    return selected.matches("a, button")
      ? selected
      : selected.querySelector("a, button");
  }

  function switchOption(state, direction) {
    if (!state.isOpen) return;
    const items = selectableItems(state);
    if (!items.length) return;
    const selected = state.resultContainer.querySelector(
      '[aria-selected="true"]'
    );
    if (direction === "up" && !selected) return;
    let next = items[0];
    if (selected) {
      const selectedIndex = items.indexOf(selected);
      next =
        direction === "up"
          ? items[(selectedIndex - 1 + items.length) % items.length]
          : items[(selectedIndex + 1) % items.length];
    }
    if (next === selected) return;
    if (!next.id) next.id = `predictive-search-option-${state.id}-${items.indexOf(next)}`;
    next.setAttribute("aria-selected", "true");
    if (selected) selected.setAttribute("aria-selected", "false");
    state.input.setAttribute("aria-activedescendant", next.id || "");
  }

  function selectOption(state) {
    const selected = selectedAction(state);
    if (selected) selected.click();
  }

  function mountForm(form) {
    if (formStateByForm.has(form)) return formStateByForm.get(form);
    const input = form.querySelector('input[type="search"]');
    const root = input && resolveRoot(form);
    const resultWrapper =
      root && root.querySelector(RESULT_WRAPPER_SELECTOR);
    const resultContainer =
      resultWrapper && resultWrapper.querySelector(RESULT_CONTAINER_SELECTOR);
    if (!input || !root || !resultWrapper || !resultContainer) return null;

    const state = {
      id: ++formSequence,
      form,
      root,
      header: form.closest(".header_search"),
      input,
      resultWrapper,
      resultContainer,
      quickSearch: root.querySelector("#quick-search"),
      sectionId: resolveSectionId(form, root),
      suggest: isSuggestSearch(form),
      timer: null,
      controller: null,
      generation: 0,
      currentQuery: input.value.trim(),
      renderedQuery: resultWrapper.classList.contains("results")
        ? normalizeQuery(input.value)
        : "",
      isOpen: input.getAttribute("aria-expanded") === "true",
      mounted: true,
      listeners: [],
    };

    listen(state, input, "input", () => scheduleQuery(state, false));
    listen(state, input, "focus", () => {
      const query = input.value.trim();
      if (!query) return;
      const normalized = normalizeQuery(requestQuery(state, query));
      if (
        resultWrapper.classList.contains("results") &&
        state.renderedQuery === normalized
      ) {
        openResults(state);
      } else if (!state.controller || state.currentQuery !== query) {
        scheduleQuery(state, true);
      }
    });
    listen(state, input, "focusout", () => {
      window.setTimeout(() => {
        if (state.mounted && !state.root.contains(document.activeElement)) {
          closeResults(state, false);
        }
      }, 0);
    });
    listen(state, input, "keyup", (event) => {
      if (!input.value.trim()) clearQuery(state);
      if (event.code === "ArrowUp" || event.code === "ArrowDown") {
        event.preventDefault();
        switchOption(state, event.code === "ArrowUp" ? "up" : "down");
      } else if (event.code === "Enter") {
        selectOption(state);
      }
    });
    listen(state, form, "submit", (event) => {
      if (
        !input.value.trim() ||
        selectedAction(state)
      ) {
        event.preventDefault();
      }
    });

    root
      .querySelectorAll('.select_cat [data-name="product_type"] li')
      .forEach((item) => {
        listen(state, item, "click", () => {
          if (item.classList.contains("active")) return;
          root
            .querySelectorAll('.select_cat [data-name="product_type"] li')
            .forEach((candidate) => candidate.classList.remove("active"));
          item.classList.add("active");
          const category = form.querySelector('[name="category"]');
          if (category) category.value = item.getAttribute("data-value") || "";
          scheduleQuery(state, true);
        });
      });

    formStateByForm.set(form, state);
    mountedFormStates.add(state);
    return state;
  }

  function disposeForm(state) {
    if (!state || !state.mounted) return;
    state.mounted = false;
    invalidateRequest(state);
    state.listeners.forEach(([target, eventName, handler, options]) => {
      target.removeEventListener(eventName, handler, options);
    });
    state.listeners.length = 0;
    setLoading(state, false);
    mountedFormStates.delete(state);
    formStateByForm.delete(state.form);
  }

  function ownedHeaders() {
    const headers = new Set();
    mountedFormStates.forEach((state) => {
      if (state.header) headers.add(state.header);
    });
    mountedScopeBindings.forEach((binding) => {
      binding.headers.forEach((header) => headers.add(header));
    });
    return headers;
  }

  function syncSearchChrome() {
    const headers = ownedHeaders();
    let hasOpenTopSearch = false;
    headers.forEach((header) => {
      const popup = header.querySelector(".popup-search");
      const open = Boolean(popup && popup.classList.contains("popup-search-show"));
      header.classList.toggle("mega-search-open", open);
      if (open && (header.classList.contains("top-search") || document.querySelector(".top-search"))) {
        hasOpenTopSearch = true;
      }
    });

    const overlay = document.querySelector(".bls__overlay");
    if (!overlay) return;
    if (hasOpenTopSearch) {
      searchOwnsOverlay = true;
      overlay.classList.add("popup-top-search");
      overlay.classList.remove("d-none-overlay");
      return;
    }
    if (!searchOwnsOverlay && !overlay.classList.contains("popup-top-search")) {
      return;
    }
    searchOwnsOverlay = false;
    overlay.classList.remove("popup-top-search");
    const collectionSidebar = document.querySelector("#CollectionSidebar");
    if (
      !collectionSidebar ||
      !collectionSidebar.classList.contains("bls__opend-popup")
    ) {
      overlay.classList.add("d-none-overlay");
    }
  }

  function handleTopSearchToggle(target, binding) {
    const toggle = target.closest(".top-search-toggle");
    if (!toggle || !scopeContains(binding.scope, toggle)) return false;
    const header = toggle.closest(".header_search");
    const drawer = header && header.querySelector('[id^="search-form"]');
    if (!drawer) return true;
    const opening = !drawer.classList.contains("bls__opend-popup-header");
    drawer.classList.toggle("bls__opend-popup-header", opening);
    document.documentElement.classList.toggle("hside_opened", opening);
    document.documentElement.classList.toggle("open-search", opening);
    if (opening) {
      const timer = window.setTimeout(() => {
        binding.timers.delete(timer);
        const input = drawer.querySelector('input[type="search"]');
        if (input) input.focus();
      }, 100);
      binding.timers.add(timer);
    }
    return true;
  }

  function handleCloseButton(target, binding) {
    const closeButton = target.closest(".mini_search_header .button-close");
    if (!closeButton || !scopeContains(binding.scope, closeButton)) return false;
    const drawer = closeButton.closest('[id^="search-form"]');
    if (drawer) drawer.classList.remove("bls__opend-popup-header");
    const header = closeButton.closest(".header_search");
    const popup = header && header.querySelector(".popup-search");
    if (popup) popup.classList.remove("popup-search-show");
    document.documentElement.classList.remove("hside_opened");
    document.documentElement.classList.remove("open-search");
    return true;
  }

  function handleUiClick(event, binding) {
    const target = event.target;
    if (!target || !target.closest) return;
    handleTopSearchToggle(target, binding);
    const closing = handleCloseButton(target, binding);
    const clickedHeader = target.closest(".header_search");

    ownedHeaders().forEach((header) => {
      if (!header.querySelector(".search-full")) return;
      const popup = header.querySelector(".popup-search");
      if (!popup) return;
      if (clickedHeader === header && !closing) {
        popup.classList.add("popup-search-show");
        if (target.classList.contains("popup-search-show")) {
          popup.classList.remove("popup-search-show");
        }
      } else {
        popup.classList.remove("popup-search-show");
      }
    });
    syncSearchChrome();
  }

  function bindingCoveredByAncestor(scope) {
    for (const binding of mountedScopeBindings) {
      if (binding.handlesUi && binding.scope !== scope && scopeContains(binding.scope, scope)) {
        return true;
      }
    }
    return false;
  }

  function mountScopeBinding(scope) {
    let binding = scopeBindingByScope.get(scope);
    if (binding) {
      binding.headers = new Set(elementsWithin(scope, ".header_search"));
      return binding;
    }
    binding = {
      scope,
      headers: new Set(elementsWithin(scope, ".header_search")),
      handlesUi: !bindingCoveredByAncestor(scope),
      timers: new Set(),
      listeners: [],
    };
    if (binding.handlesUi && scope.addEventListener) {
      const onClick = (event) => handleUiClick(event, binding);
      scope.addEventListener("click", onClick);
      binding.listeners.push([scope, "click", onClick]);
    }
    if (scope === document) {
      const onSectionLoad = (event) => api.mount(event.target);
      const onSectionUnload = (event) => api.dispose(event.target);
      document.addEventListener("shopify:section:load", onSectionLoad);
      document.addEventListener("shopify:section:unload", onSectionUnload);
      binding.listeners.push(
        [document, "shopify:section:load", onSectionLoad],
        [document, "shopify:section:unload", onSectionUnload]
      );
    }
    scopeBindingByScope.set(scope, binding);
    mountedScopeBindings.add(binding);
    return binding;
  }

  function disposeScopeBinding(binding) {
    binding.listeners.forEach(([target, eventName, handler]) => {
      target.removeEventListener(eventName, handler);
    });
    binding.listeners.length = 0;
    binding.timers.forEach((timer) => window.clearTimeout(timer));
    binding.timers.clear();
    mountedScopeBindings.delete(binding);
    scopeBindingByScope.delete(binding.scope);
  }

  const api = {
    mount: function (scope) {
      const mountScope = scope || document;
      mountScopeBinding(mountScope);
      elementsWithin(mountScope, FORM_SELECTOR).forEach(mountForm);
      syncSearchChrome();
    },

    dispose: function (scope) {
      const disposeScope = scope || document;
      Array.from(mountedFormStates).forEach((state) => {
        if (scopeContains(disposeScope, state.form)) disposeForm(state);
      });
      Array.from(mountedScopeBindings).forEach((binding) => {
        if (scopeContains(disposeScope, binding.scope)) {
          disposeScopeBinding(binding);
        }
      });
      syncSearchChrome();
    },
  };

  window.HouseOfBaboonPredictiveSearch = api;
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => api.mount(document),
      { once: true }
    );
  } else {
    api.mount(document);
  }
})(window, document);
